import React from 'react';

import FlexRow from './FlexRow';
import Input from './Input';

// A hue/saturation wheel with a separate lightness slider, plus hex and R/G/B
// boxes onto the same value. Managers arrive with a colour in whichever form
// their brand guide happens to use, so every form is an equal way in.
//
// The model is HSL, not HSV. That is what makes the slider honest: it runs
// black -> the pure hue -> white, and the middle of the track is the fully
// saturated colour. An HSV "value" slider would run black -> colour with no
// white half, and the track would not match what the thumb is sitting on.
//
// Everything here is domain-agnostic: hand it a "#RRGGBB" and it hands one
// back. Deciding what an EMPTY field means is the caller's business.

export interface PickerRGB {
  r:					number;		// 0-255
  g:					number;
  b:					number;
}

export interface PickerHSL {
  h:					number;		// 0-359
  s:					number;		// 0-100
  l:					number;		// 0-100
}

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n));

export function hexToRgb(hex: string): PickerRGB | null {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m)
    return null;

  return {
    r:	parseInt(m[1].slice(0, 2), 16),
    g:	parseInt(m[1].slice(2, 4), 16),
    b:	parseInt(m[1].slice(4, 6), 16),
  };
}

export function rgbToHex(rgb: PickerRGB): string {
  const pad = (n: number) =>
    clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');

  return ('#' + pad(rgb.r) + pad(rgb.g) + pad(rgb.b)).toUpperCase();
}

export function rgbToHsl(rgb: PickerRGB): PickerHSL {
  const r = clamp(rgb.r, 0, 255) / 255;
  const g = clamp(rgb.g, 0, 255) / 255;
  const b = clamp(rgb.b, 0, 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const span = max - min;

  const l = (max + min) / 2;

  if (span === 0)			// a grey: hue is meaningless, saturation is zero
    return { h: 0, s: 0, l: Math.round(l * 100) };

  const s =	l > 0.5
			?	span / (2 - max - min)
			:	span / (max + min);

  let h = 0;
  switch (max) {
  case r:
    h = ((g - b) / span) + (g < b ? 6 : 0);
    break;
  case g:
    h = ((b - r) / span) + 2;
    break;
  default:
    h = ((r - g) / span) + 4;
    break;
  }

  return {
    h:	Math.round(h * 60) % 360,
    s:	Math.round(s * 100),
    l:	Math.round(l * 100),
  };
}

export function hslToRgb(hsl: PickerHSL): PickerRGB {
  const h = ((hsl.h % 360) + 360) % 360 / 360;
  const s = clamp(hsl.s, 0, 100) / 100;
  const l = clamp(hsl.l, 0, 100) / 100;

  if (s === 0) {
    const grey = Math.round(l * 255);
    return { r: grey, g: grey, b: grey };
  }

  const q =	l < 0.5
			?	l * (1 + s)
			:	l + s - (l * s);

  const p = (2 * l) - q;

  const channel = (t: number) => {
    let v = t;

    if (v < 0) v += 1;
    if (v > 1) v -= 1;

    if (v < 1 / 6)	return p + ((q - p) * 6 * v);
    if (v < 1 / 2)	return q;
    if (v < 2 / 3)	return p + ((q - p) * ((2 / 3) - v) * 6);

    return p;
  };

  return {
    r:	Math.round(channel(h + (1 / 3)) * 255),
    g:	Math.round(channel(h) * 255),
    b:	Math.round(channel(h - (1 / 3)) * 255),
  };
}

// Returns a translation KEY, never a label — the caller owns the wording, so
// the wheel does not have to carry twelve strings through its props.
const hueNames: { below: number; key: string }[] = [
  { below:  15,	key: 'red' },
  { below:  45,	key: 'orange' },
  { below:  70,	key: 'yellow' },
  { below: 100,	key: 'lime' },
  { below: 150,	key: 'green' },
  { below: 175,	key: 'teal' },
  { below: 195,	key: 'cyan' },
  { below: 250,	key: 'blue' },
  { below: 280,	key: 'indigo' },
  { below: 320,	key: 'purple' },
  { below: 345,	key: 'pink' },
  { below: 361,	key: 'red' },
];

export function colourNameKey(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb)
    return 'grey';

  const hsl = rgbToHsl(rgb);

  // Lightness and saturation get the first say. At the ends of the track every
  // hue is the same colour, and calling near-black "blue" is just wrong.
  if (hsl.l <= 8)
    return 'black';

  if (hsl.l >= 95)
    return 'white';

  if (hsl.s <= 10)
    return 'grey';

  for (const entry of hueNames) {
    if (hsl.h < entry.below)
      return entry.key;
  }

  return 'red';
}

// The wheel is drawn, not painted pixel by pixel: a conic gradient carries the
// hue around the rim and a radial gradient washes the saturation out towards
// the middle. `from 90deg` puts red at 3 o'clock, which is the mapping the
// handle maths below assumes — change one and you must change the other.
//
// It is redrawn at the CURRENT lightness, which is the whole reason the wheel
// can be trusted. Zero saturation at L=50 is mid GREY, not white, so a wheel
// with a permanently white middle would promise a colour the centre does not
// produce. Painting it live also makes the brightness slider visibly move the
// wheel, instead of leaving it looking inert.
const wheelBackground = (l: number) =>
  `radial-gradient(circle closest-side, hsl(0,0%,${l}%), hsla(0,0%,${l}%,0) 100%), ` +
  'conic-gradient(from 90deg, ' +
    `hsl(0,100%,${l}%), hsl(60,100%,${l}%), hsl(120,100%,${l}%), ` +
    `hsl(180,100%,${l}%), hsl(240,100%,${l}%), hsl(300,100%,${l}%), hsl(360,100%,${l}%))`;

// Screen angle, clockwise from twelve o'clock, for a hue — and back again.
const hueToAngle = (h: number) => (h + 90) % 360;
const angleToHue = (a: number) => Math.round((a - 90 + 720) % 360);

export interface ColourPickerProps {
  value:				string;		// "#RRGGBB", always valid
  isReadOnly?:			boolean;
  onChange:				(hex: string) => any;
  name?:				string;		// label for the handle tooltip
  hexLabel:				string;
  redLabel:				string;
  greenLabel:			string;
  blueLabel:			string;
  brightnessLabel:		string;
}

const ColourPicker = (props: ColourPickerProps) => {
  const wheelRef = React.useRef(null as HTMLDivElement | null);

  const rgb = hexToRgb(props.value) || { r: 0, g: 0, b: 0 };
  const hsl = rgbToHsl(rgb);

  const emit = (next: PickerHSL) => {
    if (props.isReadOnly)
      return;

    props.onChange(rgbToHex(hslToRgb(next)));
  };

  // The hex box needs a draft of its own: "#1B6" is a legal thing to be halfway
  // through typing but not yet a colour, and re-deriving the box from the value
  // on every keystroke would eat the characters as they were typed.
  const [draft, setDraft] = React.useState(props.value);

  React.useEffect(() => {
    setDraft(props.value);
  }, [props.value]);

  const commitHex = (val: string) => {
    setDraft(val);

    const parsed = hexToRgb(val);
    if (	parsed &&
			!props.isReadOnly
		 )
      props.onChange(rgbToHex(parsed));
  };

  // Leaving a half-typed value in the box would show a colour the field does
  // not hold, so anything unparseable snaps back to the truth on blur.
  const settleHex = () => {
    if (!hexToRgb(draft))
      setDraft(props.value);
  };

  const setChannel = (key: 'r' | 'g' | 'b', raw: string) => {
    if (props.isReadOnly)
      return;

    const digits = raw.replace(/[^0-9]/g, '');
    const n = digits == '' ? 0 : clamp(parseInt(digits, 10), 0, 255);

    props.onChange(rgbToHex({ ...rgb, [key]: n }));
  };

  const pickFrom = (clientX: number, clientY: number) => {
    const el = wheelRef.current;
    if (!el)
      return;

    const box = el.getBoundingClientRect();
    const radius = box.width / 2;
    if (radius <= 0)
      return;

    const dx = clientX - (box.left + radius);
    const dy = clientY - (box.top + radius);

    // atan2(dx, -dy) reads zero straight up and grows clockwise, matching the
    // conic gradient above.
    const angle = (Math.atan2(dx, -dy) * 180) / Math.PI;

    emit({
      h:	angleToHue(angle),
      s:	clamp(Math.round((Math.sqrt((dx * dx) + (dy * dy)) / radius) * 100), 0, 100),
      // At pure black or pure white every hue looks identical, so a click on
      // the wheel would appear to do nothing at all. Meet the manager halfway
      // and step the lightness back onto the visible part of the track.
      l:	hsl.l <= 2 || hsl.l >= 98 ? 50 : hsl.l,
    });
  };

  const trackPointer = (evt: React.PointerEvent<HTMLDivElement>) => {
    if (props.isReadOnly)
      return;

    evt.preventDefault();
    evt.currentTarget.setPointerCapture(evt.pointerId);
    pickFrom(evt.clientX, evt.clientY);
  };

  const dragPointer = (evt: React.PointerEvent<HTMLDivElement>) => {
    if (	props.isReadOnly ||
			!evt.currentTarget.hasPointerCapture(evt.pointerId)
		 )
      return;

    pickFrom(evt.clientX, evt.clientY);
  };

  const angle = (hueToAngle(hsl.h) * Math.PI) / 180;
  const reach = hsl.s / 2;					// percent of the box, half-width max

  const handleLeft = 50 + (Math.sin(angle) * reach);
  const handleTop  = 50 - (Math.cos(angle) * reach);

  const channel = (key: 'r' | 'g' | 'b', label: string) => (
    <Input label={label}
           labelPlacement="outside"
           isReadOnly={props.isReadOnly}
           inputMode="numeric"
           className="w-[86px]"
           value={rgb[key].toString()}
           onValueChange={(val: string) => setChannel(key, val)} />
  );

  // The track is drawn in the CURRENT hue so the thumb always sits on the
  // colour it is about to produce.
  const trackGradient =
    `linear-gradient(to right, hsl(${hsl.h},${hsl.s}%,0%), ` +
    `hsl(${hsl.h},${hsl.s}%,50%), hsl(${hsl.h},${hsl.s}%,100%))`;

  const thumb =
    '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[20px] ' +
    '[&::-webkit-slider-thumb]:h-[20px] [&::-webkit-slider-thumb]:rounded-full ' +
    '[&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-solid ' +
    '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[--Mid-Grey] ' +
    '[&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[20px] ' +
    '[&::-moz-range-thumb]:h-[20px] [&::-moz-range-thumb]:rounded-full ' +
    '[&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-solid ' +
    '[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[--Mid-Grey]';

  return (
  <FlexRow align="start"
           className="gap-[24px] flex-wrap">
    <div className="relative shrink-0 w-[290px] h-[290px] max-w-full">
      <div ref={wheelRef}
           className={[
             'w-full h-full rounded-full',
             props.isReadOnly ? 'cursor-default opacity-60' : 'cursor-crosshair',
           ].join(' ')}
           style={{ background: wheelBackground(hsl.l), touchAction: 'none' }}
           onPointerDown={trackPointer}
           onPointerMove={dragPointer} />
      <div className="absolute pointer-events-none -translate-x-1/2 -translate-y-1/2"
           style={{ left: handleLeft + '%', top: handleTop + '%' }}>
        <div className="w-[20px] h-[20px] rounded-full border-solid border-2 border-white shadow-md"
             style={{ backgroundColor: props.value }} />
      {props.name &&
        <div className="absolute bottom-[26px] left-1/2 -translate-x-1/2 whitespace-nowrap
                        rounded-[4px] bg-white shadow-md px-[8px] py-[2px] text-[12px] !text-black">
          { props.name }
        </div>
      }
      </div>
    </div>

    <FlexRow direction="column"
             align="start"
             className="gap-[12px] min-w-[280px] flex-1">
      <FlexRow align="end"
               className="gap-[12px]">
        <div className="shrink-0 w-[56px] h-[56px] rounded-full border-solid border-1 border-[--Mid-Grey]"
             style={{ backgroundColor: props.value }} />
        <Input label={props.hexLabel}
               labelPlacement="outside"
               isReadOnly={props.isReadOnly}
               value={draft}
               onBlur={settleHex}
               onValueChange={commitHex} />
      </FlexRow>

      <FlexRow align="end"
               className="gap-[12px] flex-wrap">
        { channel('r', props.redLabel) }
        { channel('g', props.greenLabel) }
        { channel('b', props.blueLabel) }
      </FlexRow>

      <FlexRow direction="column"
               align="start"
               className="gap-[4px] pt-[4px]">
        <input type="range"
               min={0}
               max={100}
               step={1}
               disabled={props.isReadOnly}
               aria-label={props.brightnessLabel}
               className={[
                 'w-full h-[10px] appearance-none rounded-full',
                 props.isReadOnly ? 'cursor-default opacity-60' : 'cursor-pointer',
                 thumb,
               ].join(' ')}
               style={{ background: trackGradient }}
               value={hsl.l}
               onChange={(evt) => emit({ ...hsl, l: parseInt(evt.target.value, 10) })} />
        <span className="text-[--Dark-Grey] text-[12px] leading-[16px]">
          { props.brightnessLabel }
        </span>
      </FlexRow>
    </FlexRow>
  </FlexRow>
  );
};

export default ColourPicker;
